import * as React from 'react';
import { styled, useTheme } from '@glitz/react';
import ImageBase from 'webapp/components/image';
import { Carousel, useCarousel } from 'webapp/components/Carousel';
import { applyImageBackground } from './helpers';
import { isVideo } from 'webapp/components/video/checkForVideo';
import { VideoPlaceholder } from 'webapp/components/video/VideoPlaceholder';
import { Video } from 'webapp/components/video/Video';

type Props = {
  images: Excite.ContentApi.ImageApiModel[];
  activeSlide?: number;
};

export const MobileFullscreenGallery = ({ images = [], activeSlide = 0 }: Props) => {
  const carousel = useCarousel(undefined, { startIndex: activeSlide });
  const thumbCarousel = useCarousel(undefined, { slidesToScroll: 1, dragFree: true });
  const theme = useTheme()!;

  carousel.emblaApi?.on(
    'select',
    () => carousel?.emblaApi && thumbCarousel.emblaApi?.scrollTo(carousel.emblaApi?.selectedScrollSnap()),
  );

  return (
    <div>
      <Carousel {...carousel}>
        {images.map((image, index) =>
          isVideo(image.url) ? (
            <Video key={index} url={image.url} />
          ) : (
            <Image key={index} image={image} preset={'medium'} />
          ),
        )}
      </Carousel>
      <ThumbnailsCarousel
        gap={theme.sharedSpacing.xs}
        autoFill
        containerDecorator={thumbnailsContainerDecorator}
        {...thumbCarousel}
      >
        {images.length > 1 &&
          images.map((image, index) =>
            isVideo(image.url) ? (
              <ThumbnailWrapper
                key={index}
                css={index === carousel.emblaApi?.selectedScrollSnap() && currentThumbnailDecorator}
              >
                <VideoThumb
                  onClick={() => {
                    carousel.emblaApi?.scrollTo?.(index);
                    thumbCarousel.emblaApi?.scrollTo(index);
                  }}
                />
              </ThumbnailWrapper>
            ) : (
              <ThumbnailWrapper
                key={index}
                css={index === carousel.emblaApi?.selectedScrollSnap() && currentThumbnailDecorator}
              >
                <Thumbnail
                  image={image}
                  preset={'thumb'}
                  onClick={() => {
                    carousel.emblaApi?.scrollTo?.(index);
                    thumbCarousel.emblaApi?.scrollTo(index);
                  }}
                />
              </ThumbnailWrapper>
            ),
          )}
      </ThumbnailsCarousel>
    </div>
  );
};

const VideoThumb = styled(VideoPlaceholder, { width: 80, height: 80 });

const Image = styled(ImageBase, {
  width: '100%',
  height: '100%',
  maxHeight: 'calc(100vh - 100px)',
  objectFit: 'contain',
});

const ThumbnailsCarousel = styled(Carousel, {
  position: 'absolute',
  left: 0,
  bottom: 0,
  margin: { y: t => t.spacing.m },
  alignSelf: 'center',
  display: 'flex',
  height: 'auto',
});

const ThumbnailWrapper = styled.div(
  {
    position: 'relative',
    border: { xy: { width: 2, color: t => t.thumbnailBorder, style: 'solid' } },
    flexShrink: 0,
    flexGrow: 0,
    cursor: 'pointer',
  },
  applyImageBackground && {
    backgroundColor: t => t.imageBackgroundColor,
  },
);

const Thumbnail = styled(
  ImageBase,
  {
    width: 'auto',
    height: 80,
    objectFit: 'contain',
  },
  applyImageBackground && {
    mixBlendMode: 'multiply',
  },
);

const currentThumbnailDecorator = styled({
  border: { xy: { color: t => t.borderHighlight } },
});

const thumbnailsContainerDecorator = styled({
  margin: { x: t => t.sharedSpacing.m },
  display: 'flex',
});
